import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { apiUrl, fileUrl } from '../utils/api';
import { buildSafeUploadFileName } from '../utils/fileNames';
import InlineLoader from './InlineLoader';

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.bmp', '.gif', '.svg'];
const PDF_EXTENSION = '.pdf';
const COMPLEMENTARY_REPORT_PREFIX = 'Informe complementario:';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Token ${token}` } : {};
};

const getFileName = (path = '') => {
  if (!path) {
    return 'archivo';
  }

  const cleanPath = path.split('?')[0];
  const segments = cleanPath.split('/');
  return segments[segments.length - 1] || 'archivo';
};

const getLowerPath = (path = '') => path.split('?')[0].toLowerCase();
const isImageFile = (path = '') => IMAGE_EXTENSIONS.some((extension) => getLowerPath(path).endsWith(extension));
const isPdfFile = (path = '') => getLowerPath(path).endsWith(PDF_EXTENSION);

const normalizeCaseData = (caseData = {}) => ({
  diagnostico_primario: caseData.diagnostico_primario || '',
  diagnostico_secundario: caseData.diagnostico_secundario || '',
  observaciones: caseData.observaciones || '',
  archivo_caso: caseData.archivo_caso || '',
  informe_final_entregado: Boolean(caseData.informe_final_entregado),
});

const hasSavedConclusions = (caseData = {}) =>
  Boolean(
    (caseData.diagnostico_primario || '').trim() ||
      (caseData.diagnostico_secundario || '').trim() ||
      (caseData.observaciones || '').trim()
  );

const hasFinalReportStepCompleted = (caseData = {}) => Boolean(caseData.archivo_caso);

const getComplementaryReports = (caseData = {}) =>
  (caseData.documentos || []).filter((document) =>
    (document.descripcion || '').startsWith(COMPLEMENTARY_REPORT_PREFIX)
  );

const AttachmentPreview = ({ filePath, title }) => {
  if (!filePath) {
    return null;
  }

  const url = fileUrl(filePath);
  const fileName = getFileName(filePath);
  const image = isImageFile(filePath);
  const pdf = isPdfFile(filePath);

  if (!image && !pdf) {
    return (
      <div className="attachment-preview">
        <div className="attachment-preview__header">
          <strong>{title}</strong>
          <span className="attachment-preview__name">{fileName}</span>
        </div>
        <div className="attachment-preview__actions">
          <a className="btn btn-outline-brand btn-sm" href={url} target="_blank" rel="noreferrer">
            Abrir archivo
          </a>
        </div>
      </div>
    );
  }

  if (image) {
    return (
      <div className="attachment-preview">
        <div className="attachment-preview__header">
          <strong>{title}</strong>
          <span className="attachment-preview__name">{fileName}</span>
        </div>
        <img className="attachment-preview__image" src={url} alt={fileName} />
        <div className="attachment-preview__actions">
          <a className="btn btn-outline-brand btn-sm" href={url} target="_blank" rel="noreferrer">
            Abrir imagen
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="attachment-preview">
      <div className="attachment-preview__header">
        <strong>{title}</strong>
        <span className="attachment-preview__name">{fileName}</span>
      </div>
      <div className="attachment-preview__pdf-frame-wrap">
        <iframe
          className="attachment-preview__pdf"
          src={`${url}#toolbar=0&navpanes=0&scrollbar=1`}
          title={`${title} - ${fileName}`}
        />
      </div>
      <div className="attachment-preview__actions">
        <a className="btn btn-outline-brand btn-sm" href={url} target="_blank" rel="noreferrer">
          Abrir PDF
        </a>
      </div>
    </div>
  );
};

const InformeFinalForm = ({ caseId = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProtocol = location.state?.protocoloGeneral || caseId || '';
  const returnToCaseDetail = Boolean(location.state?.returnToCaseDetail);
  const [protocolOptions, setProtocolOptions] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState(initialProtocol);
  const [selectedCase, setSelectedCase] = useState(null);
  const [formData, setFormData] = useState(normalizeCaseData());
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedComplementaryFiles, setSelectedComplementaryFiles] = useState([]);
  const [complementaryReports, setComplementaryReports] = useState([]);
  const [loadingCases, setLoadingCases] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(Boolean(initialProtocol));
  const [saveNotice, setSaveNotice] = useState(null);
  const [savingConclusions, setSavingConclusions] = useState(false);
  const [savingFinalReport, setSavingFinalReport] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [conclusionsStepUnlocked, setConclusionsStepUnlocked] = useState(false);

  useEffect(() => {
    let active = true;
    setLoadingCases(true);

    axios
      .get(apiUrl('/api/ingresos_casos/?compact=1'), {
        headers: getAuthHeaders(),
      })
      .then((response) => {
        if (!active) {
          return;
        }

        setProtocolOptions(
          Array.isArray(response.data)
            ? response.data.map((item) => item?.protocolo_general).filter(Boolean)
            : []
        );
      })
      .catch((error) => {
        console.error('Error al cargar casos:', error);
        if (!active) {
          return;
        }

        toast.error('No se pudieron cargar los casos.');
        setSaveNotice({ type: 'error', text: 'No se pudieron cargar los casos para completar el cierre.' });
      })
      .finally(() => {
        if (active) {
          setLoadingCases(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedProtocol) {
      setSelectedCase(null);
      setFormData(normalizeCaseData());
      setSelectedFile(null);
      setSelectedComplementaryFiles([]);
      setComplementaryReports([]);
      setConclusionsStepUnlocked(false);
      setLoadingDetail(false);
      return;
    }

    let active = true;
    setLoadingDetail(true);
    setSaveNotice(null);

    axios
      .get(apiUrl(`/api/ingresos_casos/${selectedProtocol}/`), {
        headers: getAuthHeaders(),
      })
      .then((response) => {
        if (!active) {
          return;
        }

        setSelectedCase(response.data || null);
        setFormData(normalizeCaseData(response.data));
        setSelectedFile(null);
        setSelectedComplementaryFiles([]);
        setComplementaryReports(getComplementaryReports(response.data));
        setConclusionsStepUnlocked(hasSavedConclusions(response.data));
      })
      .catch((error) => {
        console.error('Error al cargar el caso para informe final:', error);
        if (!active) {
          return;
        }

        toast.error('No se pudo cargar el caso seleccionado.');
        setSaveNotice({ type: 'error', text: 'No se pudo cargar la información del caso para completar el cierre.' });
      })
      .finally(() => {
        if (active) {
          setLoadingDetail(false);
        }
      });

    return () => {
      active = false;
    };
  }, [selectedProtocol]);

  const finalReportStepCompleted = useMemo(() => hasFinalReportStepCompleted(formData), [formData]);
  const autogeneratedReportDisabled = Boolean(formData.archivo_caso);
  const isPackToro = selectedCase?.tipo_estudio === 'PACK_TORO';

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files?.[0] || null);
  };

  const handleComplementaryFilesChange = (event) => {
    setSelectedComplementaryFiles(Array.from(event.target.files || []));
  };

  const handleSaveConclusions = async (event) => {
    event.preventDefault();
    setSaveNotice(null);

    if (!selectedProtocol) {
      const message = 'Selecciona un caso antes de guardar las conclusiones.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return;
    }

    const payload = new FormData();
    payload.append('diagnostico_primario', formData.diagnostico_primario);
    payload.append('diagnostico_secundario', formData.diagnostico_secundario);
    payload.append('observaciones', formData.observaciones);

    setSavingConclusions(true);

    try {
      const response = await axios.patch(apiUrl(`/api/ingresos_casos/${selectedProtocol}/`), payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders(),
        },
      });

      setFormData(normalizeCaseData(response.data));
      setConclusionsStepUnlocked(true);
      const successMessage = isPackToro
        ? 'Conclusiones guardadas correctamente. Ya puedes descargar el informe Pack toro.'
        : 'Conclusiones guardadas correctamente. Ya puedes descargar el informe autogenerado y luego completar la entrega final.';
      toast.success(successMessage);
      setSaveNotice({ type: 'success', text: successMessage });
    } catch (error) {
      console.error('Error al guardar las conclusiones:', error.response?.data || error);
      const backendMessage = Object.values(error.response?.data || {})
        .flat()
        .join(' ');
      const message = backendMessage || 'No se pudieron guardar las conclusiones.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
    } finally {
      setSavingConclusions(false);
    }
  };

  const handleDownloadAutogeneratedReport = async () => {
    if (!selectedProtocol) {
      return;
    }

    try {
      setGeneratingReport(true);
      const response = await axios.get(apiUrl(`/api/ingresos_casos/${selectedProtocol}/informe-docx/`), {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = isPackToro
        ? 'informe_pack_toro_' + selectedProtocol + '.docx'
        : 'informe_autogenerado_' + selectedProtocol + '.docx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error al descargar el informe autogenerado:', error);
      const message = 'No se pudo descargar el informe autogenerado.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleSubmitFinalReport = async (event) => {
    event.preventDefault();
    setSaveNotice(null);

    if (!selectedProtocol) {
      const message = 'Selecciona un caso antes de guardar la entrega del informe final.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return;
    }

    if (!conclusionsStepUnlocked) {
      const message = 'Primero guarda las conclusiones del caso.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return;
    }

    const payload = new FormData();
    payload.append('informe_final_entregado', String(formData.informe_final_entregado));
    if (selectedFile) {
      payload.append('archivo_caso', selectedFile, buildSafeUploadFileName(selectedFile.name));
    }

    setSavingFinalReport(true);

    try {
      const response = await axios.patch(apiUrl(`/api/ingresos_casos/${selectedProtocol}/`), payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders(),
        },
      });

      const uploadedComplementaryReports = [];
      for (const file of selectedComplementaryFiles) {
        const documentPayload = new FormData();
        documentPayload.append('protocolo_general', selectedProtocol);
        documentPayload.append('descripcion', `${COMPLEMENTARY_REPORT_PREFIX} ${file.name}`);
        documentPayload.append('archivo', file, buildSafeUploadFileName(file.name));
        const documentResponse = await axios.post(apiUrl('/api/documentos/'), documentPayload, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...getAuthHeaders(),
          },
        });
        uploadedComplementaryReports.push(documentResponse.data);
      }

      setFormData(normalizeCaseData(response.data));
      setSelectedFile(null);
      setSelectedComplementaryFiles([]);
      setComplementaryReports((current) => [...current, ...uploadedComplementaryReports]);
      const successMessage = uploadedComplementaryReports.length
        ? `Entrega e informe final guardados correctamente. Se cargaron ${uploadedComplementaryReports.length} informe(s) complementario(s).`
        : 'Entrega e informe final guardados correctamente.';
      toast.success(successMessage);
      setSaveNotice({ type: 'success', text: successMessage });

      if (returnToCaseDetail) {
        navigate(`/ver-caso/${selectedProtocol}`, {
          state: { bannerMessage: successMessage },
        });
      }
    } catch (error) {
      console.error('Error al guardar la entrega del informe final:', error.response?.data || error);
      const backendMessage = Object.values(error.response?.data || {})
        .flat()
        .join(' ');
      const message = backendMessage || 'No se pudo guardar la entrega del informe final.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
    } finally {
      setSavingFinalReport(false);
    }
  };

  if (loadingCases || loadingDetail) {
    return (
      <div className="surface-card">
        <InlineLoader label={loadingDetail ? 'Cargando diagnóstico e informe final...' : 'Cargando casos...'} />
      </div>
    );
  }

  return (
    <div className="surface-card">
      <div className="form-highlight mb-4">
        <div>
          <span className="section-kicker">Cierre</span>
          <h3 className="form-title">Diagnóstico e informe final del caso</h3>
          <p className="page-subtitle mt-2 mb-0">
            Primero guarda las conclusiones, luego descarga el informe autogenerado y recién después registra la entrega y carga el archivo final.
          </p>
        </div>
        <div className="protocol-chip">{selectedProtocol || 'Sin caso seleccionado'}</div>
      </div>

      <div className="result-form-grid mb-4">
        <div className="result-form-grid__main">
          {saveNotice ? (
            <div className={`alert ${saveNotice.type === 'success' ? 'alert-success' : 'subtle-alert'} mb-4`} role="status">
              {saveNotice.text}
            </div>
          ) : null}

          <div className="mb-4">
            <label className="form-label">Protocolo general</label>
            <select
              className="form-select"
              value={selectedProtocol}
              onChange={(event) => setSelectedProtocol(event.target.value)}
              required
            >
              <option value="">Seleccione un caso...</option>
              {protocolOptions.map((protocol) => (
                <option key={protocol} value={protocol}>
                  {protocol}
                </option>
              ))}
            </select>
          </div>

          {selectedProtocol ? (
            <>
              <form onSubmit={handleSaveConclusions} className="mb-4">
                <div className="result-summary-card">
                  <div className="final-step-card__header">
                    <span className="section-kicker">Paso 1</span>
                    <span
                      className={`final-step-badge ${
                        conclusionsStepUnlocked ? 'final-step-badge--complete' : 'final-step-badge--pending'
                      }`}
                    >
                      {conclusionsStepUnlocked ? 'Paso completado' : 'Pendiente'}
                    </span>
                  </div>
                  <h4 className="result-summary-card__title">Guardar conclusiones del caso</h4>
                  <p className="result-summary-card__text">
                    Registra el diagnóstico primario, secundario y las conclusiones. Al guardar se habilita la descarga del informe autogenerado.
                  </p>

                  <div className="row mb-3 mt-1">
                    <div className="col-md-6 mb-3 mb-md-0">
                      <label className="form-label">Diagnóstico primario</label>
                      <input
                        type="text"
                        className="form-control"
                        name="diagnostico_primario"
                        value={formData.diagnostico_primario}
                        onChange={handleChange}
                        placeholder="Ej. intoxicación, infección, neoplasia"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Diagnóstico secundario</label>
                      <input
                        type="text"
                        className="form-control"
                        name="diagnostico_secundario"
                        value={formData.diagnostico_secundario}
                        onChange={handleChange}
                        placeholder="Ej. diagnóstico complementario o diferencial"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Conclusiones</label>
                    <textarea
                      className="form-control"
                      name="observaciones"
                      rows="4"
                      value={formData.observaciones}
                      onChange={handleChange}
                      placeholder="Agrega conclusiones, aclaraciones o comentarios finales."
                    />
                  </div>

                  <div className="d-flex justify-content-end pt-2">
                    <button type="submit" className="btn btn-brand" disabled={savingConclusions}>
                      {savingConclusions ? 'Guardando conclusiones...' : 'Guardar conclusiones'}
                    </button>
                  </div>
                </div>
              </form>

              {conclusionsStepUnlocked ? (
                <>
                  <div className="result-summary-card mb-4">
                    <div className="final-step-card__header">
                      <span className="section-kicker">Paso 2</span>
                      <span
                        className={`final-step-badge ${
                          autogeneratedReportDisabled ? 'final-step-badge--pending' : 'final-step-badge--ready'
                        }`}
                      >
                        {autogeneratedReportDisabled ? 'No disponible' : 'Disponible'}
                      </span>
                    </div>
                    <h4 className="result-summary-card__title">
                      {isPackToro ? 'Descargar informe Pack toro' : 'Descargar informe autogenerado'}
                    </h4>
                    <p className="result-summary-card__text">
                      {isPackToro
                        ? 'Las conclusiones ya fueron guardadas. El informe incluirá la tabla de resultados Pack toro, el resumen y el bloque institucional.'
                        : 'Con las conclusiones ya guardadas, puedes descargar el informe autogenerado del caso para revisarlo o usarlo como base.'}
                    </p>

                    {isPackToro && !autogeneratedReportDisabled ? (
                      <div className="alert subtle-alert subtle-alert--info mb-3" role="status">
                        <strong>Informe Pack toro autogenerado disponible.</strong> Ya puedes descargar el documento DOCX con las conclusiones actualizadas.
                      </div>
                    ) : null}

                    {autogeneratedReportDisabled ? (
                      <div className="alert subtle-alert subtle-alert--info mb-0">
                        La descarga del informe autogenerado queda desactivada porque este caso ya tiene un informe final cargado.
                      </div>
                    ) : null}

                    <div className="d-flex justify-content-end pt-2">
                      <button
                        type="button"
                        className="btn btn-brand final-report-download-btn"
                        onClick={handleDownloadAutogeneratedReport}
                        disabled={generatingReport || autogeneratedReportDisabled}
                      >
                        <span className="final-report-download-btn__label">
                          {autogeneratedReportDisabled
                            ? `${isPackToro ? 'Informe Pack toro' : 'Informe autogenerado'} no disponible`
                            : generatingReport
                              ? `Generando informe ${isPackToro ? 'Pack toro' : 'autogenerado'}...`
                              : `Descargar informe ${isPackToro ? 'Pack toro' : 'autogenerado'}`}
                        </span>
                        <span className="final-report-download-btn__ext">DOCX</span>
                      </button>
                    </div>
                  </div>

                  <form onSubmit={handleSubmitFinalReport}>
                    <div className="result-summary-card">
                      <div className="final-step-card__header">
                        <span className="section-kicker">Paso 3</span>
                        <span
                          className={`final-step-badge ${
                            finalReportStepCompleted ? 'final-step-badge--complete' : 'final-step-badge--pending'
                          }`}
                        >
                          {finalReportStepCompleted ? 'Paso completado' : 'Pendiente'}
                        </span>
                      </div>
                      <h4 className="result-summary-card__title">Entrega, informe final y complementarios</h4>
                      <p className="result-summary-card__text">
                        Cuando corresponda, marca la entrega del informe final y adjunta el archivo definitivo del caso.
                      </p>

                      <div className="mb-4">
                        <label className="form-label d-block">Entrega del informe final</label>
                        <div className="d-flex gap-2 flex-wrap">
                          <button
                            type="button"
                            className={`btn ${formData.informe_final_entregado ? 'btn-success' : 'btn-outline-success'}`}
                            onClick={() => setFormData((prev) => ({ ...prev, informe_final_entregado: true }))}
                          >
                            Sí, entregado
                          </button>
                          <button
                            type="button"
                            className={`btn ${!formData.informe_final_entregado ? 'btn-danger' : 'btn-outline-danger'}`}
                            onClick={() => setFormData((prev) => ({ ...prev, informe_final_entregado: false }))}
                          >
                            No entregado
                          </button>
                        </div>
                        <div className="form-text mt-2">
                          Marca si el informe final ya fue entregado al propietario o al médico remitente.
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="form-label">Informe final</label>
                        <input
                          type="file"
                          className="form-control"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                          onChange={handleFileChange}
                        />
                        <div className="form-text mt-2">
                          Puedes adjuntar el informe final del caso en PDF, Word o imagen. Este archivo es distinto del informe autogenerado del sistema.
                        </div>

                        {formData.archivo_caso ? (
                          <div className="mt-3">
                            <AttachmentPreview filePath={formData.archivo_caso} title="Vista previa del informe final" />
                          </div>
                        ) : null}

                        {selectedFile ? (
                          <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
                            Nuevo archivo seleccionado: {selectedFile.name}
                          </div>
                        ) : null}
                      </div>

                      <div className="mb-4">
                        <label className="form-label">Informes complementarios</label>
                        <input
                          type="file"
                          className="form-control"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.webp"
                          multiple
                          onChange={handleComplementaryFilesChange}
                        />
                        <div className="form-text mt-2">
                          Puedes seleccionar uno o varios estudios, anexos o informes adicionales. Se conservarán como archivos independientes del informe final.
                        </div>

                        {selectedComplementaryFiles.length > 0 ? (
                          <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
                            {selectedComplementaryFiles.length} archivo(s) complementario(s) seleccionado(s):{' '}
                            {selectedComplementaryFiles.map((file) => file.name).join(', ')}
                          </div>
                        ) : null}

                        {complementaryReports.length > 0 ? (
                          <div className="mt-3 d-grid gap-3">
                            {complementaryReports.map((document, index) => (
                              <AttachmentPreview
                                key={document.id || index}
                                filePath={document.archivo}
                                title={(document.descripcion || '').replace(`${COMPLEMENTARY_REPORT_PREFIX} `, '') || `Informe complementario ${index + 1}`}
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>

                      <div className="d-flex justify-content-end gap-2 pt-2">
                        <Link to={`/ver-caso/${selectedProtocol}`} className="btn btn-outline-brand">
                          Ver caso
                        </Link>
                        <button type="submit" className="btn btn-brand" disabled={savingFinalReport}>
                          {savingFinalReport ? 'Guardando...' : 'Guardar entrega e informes'}
                        </button>
                      </div>
                    </div>
                  </form>
                </>
              ) : (
                <div className="alert subtle-alert subtle-alert--info mb-0">
                  Guarda primero las conclusiones para habilitar la descarga del informe autogenerado y la carga del informe final.
                </div>
              )}
            </>
          ) : (
            <div className="alert subtle-alert subtle-alert--info mb-0">
              Selecciona un caso para completar su diagnóstico e informe final.
            </div>
          )}
        </div>

        <aside className="result-form-grid__aside">
          <div className="result-summary-card">
            <span className="section-kicker">Resumen</span>
            <h4 className="result-summary-card__title">Caso seleccionado</h4>
            <p className="result-summary-card__text">
              El cierre ahora se resuelve en tres pasos: guardar conclusiones, descargar el informe autogenerado y luego registrar la entrega con el archivo final.
            </p>

            <dl className="result-summary-list">
              <div>
                <dt>Protocolo</dt>
                <dd>{selectedProtocol || 'Sin seleccionar'}</dd>
              </div>
              <div>
                <dt>Propietario</dt>
                <dd>{selectedCase?.propietario || 'Sin datos'}</dd>
              </div>
              <div>
                <dt>Tipo de estudio</dt>
                <dd>{selectedCase?.tipo_estudio_nombre || selectedCase?.tipo_estudio || 'Sin datos'}</dd>
              </div>
              <div>
                <dt>Muestras</dt>
                <dd>{selectedCase?.muestras?.length || 0}</dd>
              </div>
              <div>
                <dt>Conclusiones guardadas</dt>
                <dd>{conclusionsStepUnlocked ? 'Sí' : 'Pendiente'}</dd>
              </div>
              <div>
                <dt>Estado de entrega</dt>
                <dd>{formData.informe_final_entregado ? 'Entregado' : 'Pendiente'}</dd>
              </div>
            </dl>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default InformeFinalForm;
